/** @typedef {'va' | 'trustee' | 'guardianship' | 'probate'} BondRetrievalKind */
/** @typedef {'va' | 'standard'} ObligeeResolverStrategy */

/**
 * Canonical API segments and nesting for bond retrieval pages.
 * Paths match existing hooks (do not rename endpoints).
 *
 * @type {Record<BondRetrievalKind, {
 *   nestedKey: string,
 *   bondTypeId?: number,
 *   retrievePath: (bondId: string) => string,
 *   updatePath: (bondId: string) => string,
 *   approvePath: (bondId: string) => string,
 *   approveGenerateStatus: string,
 *   bondNumberApproveContext: 'va_billing_principal' | 'law_firm',
 *   correctionEntityKey: string,
 *   correctionBondType: string,
 *   correctionPrincipalField: string,
 *   correctionNavigatePath: (bondId: string) => string,
 *   correctionEmailSubject: string,
 *   obligeeResolverStrategy: ObligeeResolverStrategy,
 *   obligeeResolverLogLabel?: string,
 *   fetchBondUpdatesOnMount?: boolean,
 * }>}
 */
export const bondRetrievalPageConfig = {
  va: {
    nestedKey: "vabond",
    bondTypeId: 6,
    retrievePath: (bondId) => `/retrieve-va-bond/${bondId}/`,
    updatePath: (bondId) => `/update_va_bond/${bondId}/`,
    approvePath: (bondId) => `/approve_va_bond/${bondId}/`,
    approveGenerateStatus: "Approved",
    bondNumberApproveContext: "va_billing_principal",
    correctionEntityKey: "vabond",
    correctionBondType: "VA",
    correctionPrincipalField: "Principals",
    correctionNavigatePath: (bondId) => `vermost-va-bond-update/${bondId}`,
    correctionEmailSubject: "Notification: Data Correction Used for VA Bond",
    obligeeResolverStrategy: "va",
  },
  trustee: {
    nestedKey: "trustee_bond",
    bondTypeId: 4,
    retrievePath: (bondId) => `/retrieve-trustee-bond/${bondId}/`,
    updatePath: (bondId) => `/update_trustee_bond/${bondId}/`,
    approvePath: (bondId) => `/approve_trustee_bond/${bondId}/`,
    approveGenerateStatus: "Approved",
    bondNumberApproveContext: "law_firm",
    correctionEntityKey: "trustee_bond",
    correctionBondType: "Trustee",
    correctionPrincipalField: "Trustees",
    correctionNavigatePath: (bondId) => `vermost-trustee-bond-update/${bondId}`,
    correctionEmailSubject: "Notification: Data Correction Used for Trustee Bond",
    obligeeResolverStrategy: "standard",
    obligeeResolverLogLabel: "TrusteeBondRetrieve",
  },
  guardianship: {
    nestedKey: "guardianshipbond",
    bondTypeId: 3,
    retrievePath: (bondId) => `/retrieve-guardianship-bond/${bondId}/`,
    updatePath: (bondId) => `/update_guardianship_bond/${bondId}/`,
    approvePath: (bondId) => `/approve_guardianship_bond/${bondId}/`,
    approveGenerateStatus: "Active",
    bondNumberApproveContext: "law_firm",
    correctionEntityKey: "guardianshipbond",
    correctionBondType: "Guardianship",
    correctionPrincipalField: "Guardians",
    correctionNavigatePath: (bondId) => `vermost-guardianship-bond-update/${bondId}`,
    correctionEmailSubject: "Notification: Data Correction Used for Guardianship Bond",
    obligeeResolverStrategy: "standard",
    obligeeResolverLogLabel: "GuardianshipBondRetrieve",
    fetchBondUpdatesOnMount: true,
  },
  probate: {
    nestedKey: "probatebond",
    bondTypeId: 1,
    retrievePath: (bondId) => `/retrieve-probate-bond/${bondId}/`,
    updatePath: (bondId) => `/update_probate_bond/${bondId}/`,
    approvePath: (bondId) => `/approve_probate_bond/${bondId}/`,
    approveGenerateStatus: "Active",
    bondNumberApproveContext: "law_firm",
    correctionEntityKey: "probatebond",
    correctionBondType: "Probate",
    correctionPrincipalField: "Principals",
    correctionNavigatePath: (bondId) => `vermost-probate-bond-update/${bondId}`,
    correctionEmailSubject: "Notification: Data Correction Used for Probate Bond",
    obligeeResolverStrategy: "standard",
    obligeeResolverLogLabel: "ProbateBondRetrieve",
    fetchBondUpdatesOnMount: true,
  },
};
